import {
  Button,
  HStack,
  Input,
  VStack,
  Text,
  FormLabel,
  Select,
  Spinner,
  Grid,
} from "@chakra-ui/react";
import { FractionChildProps, FractionProps, PostFraction } from "../Fraction";
import { useCallback, useEffect, useState } from "react";
import { DisplayMode } from "../../Post";
import { toast } from "react-toastify";
import {
  FailReasonE,
  Query,
  QueryClient,
  QueryDto_PUT_POST,
  QueryResponseDto_GET,
} from "../../../../../Api/client/QueryClient";
import { OfiDateTimePicker } from "../../../../Helpers/Time";
import { useAwaitResponse } from "../../../../../Api/HttpRequestHelpers";
import { Competition_GET } from "../../../../../Api/dto/Competition_GET";
import {
  CompetitionClient,
  DisciplineClient,
} from "../../../../../Api/client/CompetitionClient";
import { Discipline_GET } from "../../../../../Api/dto/Discipline_GET";
import { MultiSelectNumber } from "../../../../Helpers/MultiSelect";
import { Role, useSessionState } from "../../../../../Session/SessionService";
import { LoginModalBody } from "../../../../../Session/LoginModal";

// QueryDb:
// id,
// constarint_max_answer_count
// constraint_roles
// constraint_signup_for_competition_ids
// constraint_signup_for_discipline_ids
//
// question
// options
//

// QueryResponseDb
// id;
// queryId,
// authAs:
// signup_id:
// answer:

// DTO with queryDb info
// status code [notallowed] + reason "no_singup" or not_authenticated or "not_authorized"
// OR
// status code 200 + {
//   max_total_count: 123
//   total_count: 111
//   question: "Select your T-Shirt!"
//   options: ["xs", "s", "m"]
//   queries: [{answer_id?, signup_id?, Name?, old_answer? }]
// }

// QueryFractionDefinition
// queryId,
// answer_prompt,
// "no_answer" option  (send one DTO per answer, do not send one for no_answer)
// explain_no_signup
// explain_not_authenticated
// explain_not_right_role
//

export type QueryFractionState =
  | undefined
  | {
      queryId: number;
      answerPrompt: string;
      noAnswerOption: string;
      errorExplanation: {
        noSignup: string; // Du måste anmäla dig innan du kan få en T-Shirt
        notAuthenticated: string; // Du måste logga in med email adressen du angav vid anmälan först
        maxAnswerCountReached: string; // Alla T-Shirtar är utdelade
        answerTimeoutReached: string; // Försend för att få en T-Shirt, beställningen är lagd.
      };
    };
// Fraction:
// id,
// optionalOverallQuestion:
// notAuthorizedText:
// notSignedUpText:
// id ----> QueryDefinition    NOW_IT_IS_DESPLAYABLE, if conditional then signin needed
//  NOT_SIGNED_IN ----> "För att ge svar så måste du vara inloggad"
//                                                 [Boka T-Shirt]
//
//  SIGNED_IN -->
//  Jan Pettersson, T-shirt storlek: [ ... ]
//  Jan Pettersson2, T-shirt storlek: [ ... ]
//
//
//
//  SignedIn   getAnswerForm --->  {signupId: 1, signupName: "Jan Pettersson", "], prevAnswer: ""}
//

export interface QueryState {
  loading: boolean;
  queryLoadedFromBackend: QueryResponseDto_GET | null;
}

interface FractionModeProps {
  fractionProps: FractionChildProps<QueryFractionState>;
  queryState: QueryState;
}

function QueryEditorAndViewer(props: FractionChildProps<QueryFractionState>) {
  const [queryState, setQueryState] = useState<QueryState>({
    loading: false,
    queryLoadedFromBackend: null,
  });

  const loadQueryFromBackend = useCallback(
    (queryId: number) => {
      setQueryState((old) => ({ ...old, loading: true }));
      QueryClient.getQuery(queryId)
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            setQueryState((old) => ({
              ...old,
              queryLoadedFromBackend: response.body || null,
            }));
            console.log(response, "JR496");
          }
        })
        .finally(() => {
          setQueryState((old) => ({ ...old, loading: false }));
        });
    },
    [setQueryState]
  );

  useEffect(() => {
    if (props.state?.queryId) {
      loadQueryFromBackend(props.state.queryId);
    }
  }, [props.state?.queryId]);

  //if (state.loading) {
  //return <Text fontStyle={"italic"}>Hämtar innehållet...</Text>;
  //}
  //else if (props.mode === DisplayMode.EDIT) {
  //return <QueryEditor {...props} />;
  //}
  ////else if (state.queryLoadedFromBackend == null) {
  ////return (
  //<Text textColor={"red"} fontStyle={"italic"}>
  //404: Inget innehåll hittades
  //</Text>
  //);
  //}
  if (props.state?.queryId && queryState.queryLoadedFromBackend == null) {
    if (queryState.loading) {
      return <Text>Laddar...</Text>;
    } else {
      return <Text>Innehållet saknas</Text>;
    }
  }

  if (props.mode === DisplayMode.EDIT) {
    return <QueryEditor fractionProps={props} queryState={queryState} />;
  } else {
    return (
      <QueryViewer
        fractionProps={props}
        queryState={queryState}
        reloadFraction={loadQueryFromBackend}
      />
    );
  }
}

function QueryEditor(props: FractionModeProps) {
  const [queryId, setQueryId] = useState<number | null>(
    props.fractionProps?.state?.queryId ?? null
  );

  const [dbState, setDbState] = useState<QueryDto_PUT_POST>({
    max_total_count: props.queryState.queryLoadedFromBackend?.max_total_count,
    signupRequiredToCompetitionId:
      props.queryState.queryLoadedFromBackend?.requiredCompetitionId,
    signupRequiredToDisciplineIds:
      props.queryState.queryLoadedFromBackend?.requiredDisciplineId || [],
    options: props.queryState.queryLoadedFromBackend?.options || [],
    question:
      props.queryState.queryLoadedFromBackend?.question ?? "Välj en T-Shirt!",
    lastAnswerTimeInMs:
      props.queryState.queryLoadedFromBackend?.lastAnswerTimeInMs ?? null,
  } as QueryDto_PUT_POST);

  const [fractionState, setFractionState] = useState<{
    queryId: number | null;
    answerPrompt: string;
    noAnswerOption: string;
    errorExplanation: {
      noSignup: string;
      notAuthenticated: string;
      maxAnswerCountReached: string;
      answerTimeoutReached: string;
    };
  }>(
    props.fractionProps.state ?? {
      queryId: null,
      answerPrompt: "Ditt svar/val:",
      noAnswerOption: "Inget val",
      errorExplanation: {
        answerTimeoutReached: "Tiden har gått ut, vi tar inte emot fler svar.",
        maxAnswerCountReached:
          "Vi har redan nått vår gräns och tar inte emot fler svar.",
        noSignup: "Du måste anmäla dig innan du kan göra ett val här.",
        notAuthenticated: "Du måste logga in för att göra ett val här.",
      },
    }
  );

  useEffect(() => {
    if (queryId) {
      setFractionState((old) => ({ ...old, queryId: queryId }));
    }
  }, [queryId]);

  useEffect(() => {
    if (fractionState.queryId && props.fractionProps.onChange) {
      props.fractionProps.onChange({
        ...fractionState,
        queryId: fractionState.queryId,
      });
    }
  }, [fractionState]);

  const persistDbState = useCallback(() => {
    if (queryId == null) {
      QueryClient.postQuery(dbState)
        .then((response) => {
          setQueryId(response?.queryId || null);
        })
        .then(() => {
          toast("De blå fälten är nu sparade till databasen");
        });
    } else {
      QueryClient.putQuery(queryId, dbState).then(() => {
        toast("De blå fälten är nu sparade till databasen");
      });
    }
  }, [dbState, queryId]);

  //////queryId: number;
  //answerPrompt: string;
  //noAnswerOption: string;
  //errorExplanation: {
  //noSignup: string; // Du måste anmäla dig innan du kan få en T-Shirt
  //notCorrectRole: string; // Du saknar rätt rättigheter
  //notAuthenticated: string; // Du måste logga in med email adressen du angav vid anmälan först
  //answerTimeoutReached: string; // Försend för att få en T-Shirt, beställningen är lagd.
  //};
  //max_total_count: number | null;
  //errorExplanation.maxAnswerCountReached: string; // Alla T-Shirtar är utdelade
  //question: string;       // "Select your T-Shirt!"
  //options: Array<string>; // ["xs", "s", ...]
  //updatable: queries: Array<Query>;
  // Question:
  return (
    <VStack borderColor={"gray.500"}>
      <FormLabel>Fråga: </FormLabel>
      <Input
        borderWidth={"2px"}
        borderColor={"blue"}
        borderStyle={"dashed"}
        value={dbState.question}
        onChange={(e) => {
          setDbState((old) => {
            return { ...old, question: e.target.value };
          });
        }}
      />
      {/** 
       * 
      <FormLabel>Svars prompt: </FormLabel>
      <Input
        value={fractionState.answerPrompt}
        onChange={(e) => {
          setFractionState((old) => ({
            ...old,
            answerPrompt: e.target.value ?? "",
          }));
        }}
      />
      */}
      <FormLabel>Svarsalternativ separerade med komma (,): </FormLabel>
      <Input
        borderWidth={"2px"}
        borderColor={"blue"}
        borderStyle={"dashed"}
        value={dbState.options.join(",")}
        onChange={(e) => {
          setDbState((old) => {
            return { ...old, options: (e.target.value || "").split(",") };
          });
        }}
      />
      <FormLabel>Inget val visas som: </FormLabel>
      <Input
        value={fractionState.noAnswerOption}
        onChange={(e) => {
          setFractionState((old) => {
            return {
              ...old,
              noAnswerOption: e.target.value,
            };
          });
        }}
      />
      <FormLabel>Max antal svar: </FormLabel>
      <Input
        borderWidth={"2px"}
        borderColor={"blue"}
        borderStyle={"dashed"}
        value={dbState.max_total_count ?? undefined}
        onChange={(e) => {
          setDbState((old) => {
            return {
              ...old,
              max_total_count: convertToIntegerOrNull(e.target.value),
            };
          });
        }}
      />
      <OfiDateTimePicker
        borderWidth={"2px"}
        borderColor={"blue"}
        borderStyle={"dashed"}
        selected={
          dbState.lastAnswerTimeInMs == null
            ? null
            : new Date(dbState.lastAnswerTimeInMs)
        }
        selectsEnd={true}
        startDate={new Date()} // Current time in Date format
        endDate={
          dbState.lastAnswerTimeInMs == null
            ? null
            : new Date(dbState.lastAnswerTimeInMs) //
        }
        onChange={(selectedDate) => {
          setDbState((old) => {
            return {
              ...old,
              lastAnswerTimeInMs: selectedDate ? selectedDate.getTime() : null,
            };
          });
        }}
      />
      <HStack hidden={dbState.lastAnswerTimeInMs == null}>
        <Input
          width={"90%"}
          placeholder={"Hjälp text: För sent för att svara"}
          value={fractionState.errorExplanation.answerTimeoutReached}
          onChange={(e) => {
            setFractionState((old) => {
              return { ...old, errorExplanation: {...old.errorExplanation, answerTimeoutReached: e.target.value || ""}}; // prettier-ignore
            });
          }}
        />
      </HStack>

      <FormLabel>Anmälan till följande krävs för att få svara:</FormLabel>
      <SignupSelector
        competitionIdValue={dbState.signupRequiredToCompetitionId}
        disciplineIdsValue={dbState.signupRequiredToDisciplineIds}
        onChange={(
          competitionId: number | null,
          disciplineIds: Array<number>
        ) => {
          setDbState((old) => ({
            ...old,
            signupRequiredToCompetitionId: competitionId,
            signupRequiredToDisciplineIds: disciplineIds,
          }));
        }}
      />

      <Input
        hidden={dbState.signupRequiredToCompetitionId == null}
        width={"90%"}
        placeholder={"Hjälp text: Anmälan till tävling saknas"}
        value={fractionState.errorExplanation.noSignup}
        onChange={(e) => {
          setFractionState((old) => {
            return { ...old, errorExplanation: {...old.errorExplanation, noSignup: e.target.value || ""}}; // prettier-ignore
          });
        }}
      />

      <Input
        width={"90%"}
        placeholder={"Hjälp text: Har nått max antal svar"}
        value={fractionState.errorExplanation.maxAnswerCountReached}
        onChange={(e) => {
          setFractionState((old) => {
            return { ...old, errorExplanation: {...old.errorExplanation, maxAnswerCountReached: e.target.value || ""}}; // prettier-ignore
          });
        }}
      />

      <Input
        width={"90%"}
        placeholder={"Hjälp text: Du måste logga in för att svara"}
        value={fractionState.errorExplanation.notAuthenticated}
        onChange={(e) => {
          setFractionState((old) => {
            return { ...old, errorExplanation: {...old.errorExplanation, notAuthenticated: e.target.value || ""}}; // prettier-ignore
          });
        }}
      />

      <Button
        width={"80%"}
        borderWidth={"2px"}
        borderColor={"blue"}
        borderStyle={"dashed"}
        colorScheme="blue"
        background={"blue.100"}
        fontSize={"30px"}
        textColor={"black"}
        _hover={{ background: "blue.200" }}
        padding={"20px"}
        margin={"80px"}
        onClick={() => persistDbState()}
      >
        Spara alla blå fält
      </Button>
    </VStack>
  );
}
//answerTimeoutReached: "Tiden har gått ut, vi tar inte emot fler svar.",
//maxAnswerCountReached: "Vi har redan nått vår gräns och tar inte emot fler
//svar.", noSignup: "Du måste anmäla dig innan du kan göra ett val här.",
//notAuthenticated: "Du måste logga in för att göra ett val här.",
//notCorrectRole: "Du saknar rättigheter för att svara.",

function detectAnswerChange(answerEntry: Query, allQueries: Array<Query>) {
  const wasAlreadyPersisted = answerEntry.answerId != null;
  if (!wasAlreadyPersisted) {
    return answerEntry.answer == null ? null : "POST";
  }

  const matchingQueries =
    allQueries.filter((q) => q.signupId === answerEntry.answerId) || [];
  const hasChange =
    matchingQueries.length === 0 ||
    answerEntry.answer !== matchingQueries[0].answer;

  if (hasChange) {
    return answerEntry.answer === null ? "DELETE" : "PUT";
  }

  return null;
}

function QueryViewer(
  props: FractionModeProps & { reloadFraction: (queryId: number) => void }
) {
  const session = useSessionState();
  const [answers, setAnswers] = useState<Array<Query>>([]);
  const [changesMade, setChangesMade] = useState<boolean>(false);
  const [awaitingUpdates, setAwaitingUpdates] = useState<boolean>(false);
  const [makeChanges, setMakeChanges] = useState<boolean>(false);
  const [updatable, setUpdatable] = useState<boolean>(false);

  useEffect(() => {
    const queries = props.queryState.queryLoadedFromBackend?.queries || [];
    setAnswers(
      queries.map((q) => ({
        ...q,
      }))
    );
    setUpdatable(props.queryState.queryLoadedFromBackend?.updatable ?? false);
  }, [
    props.queryState.queryLoadedFromBackend?.queries,
    props.queryState.queryLoadedFromBackend?.updatable,
    setUpdatable,
    setAnswers,
  ]);

  useEffect(() => {
    const changeWasMade = answers.some((a) => {
      return (
        null !=
        detectAnswerChange(
          a,
          props.queryState.queryLoadedFromBackend?.queries || []
        )
      );
    });

    setChangesMade(changeWasMade);
  }, [answers, setChangesMade]);

  const queryId = props.fractionProps.state?.queryId;
  const noAnswerOption = props.fractionProps.state?.noAnswerOption;
  const backendDto = props.queryState.queryLoadedFromBackend;

  if (props.fractionProps == null) {
    return <Text>[Lägg till fråga]</Text>;
  } else if (backendDto == null) {
    if (props.queryState.loading) {
      return <Text>Laddar...</Text>;
    } else {
      return <Text>404 Not found</Text>;
    }
  } else if (backendDto.error == null) {
    if (makeChanges) {
      return (
        <VStack width={"100%"}>
          <Text fontSize={"24px"}>{backendDto?.question}</Text>
          {answers.map((a) => {
            return (
              <VStack key={a.signupId} gap={1} width="100%" align={"left"}>
                <Text flexShrink={0} marginTop={4}>
                  {a.signupName}
                </Text>
                <AnswerInput
                  onChange={(ans: string | null) => {
                    setAnswers((old) =>
                      old.map((a1) =>
                        a1.signupId === a.signupId ? { ...a1, answer: ans } : a1
                      )
                    );
                  }}
                  noAnswerOption={noAnswerOption ?? null}
                  options={backendDto?.options ?? []}
                  value={a.answer ?? undefined}
                />
              </VStack>
            );
          })}

          <HStack marginTop={"40px"}>
            <Button
              width={"50%"}
              onClick={() => {
                setAnswers(
                  (props.queryState.queryLoadedFromBackend?.queries || []).map(
                    (q) => ({
                      ...q,
                    })
                  )
                );
                setMakeChanges(false);
              }}
            >
              Avbryt
            </Button>
            <Button
              paddingX={"50px"}
              hidden={!changesMade}
              isDisabled={awaitingUpdates || !changesMade}
              colorScheme="pink"
              onClick={async () => {
                if (awaitingUpdates || !changesMade) {
                  return;
                } else if (queryId == null) {
                  console.error("queryId null");
                  return;
                }

                setAwaitingUpdates(true);

                for (const a of answers) {
                  const changeType = detectAnswerChange(
                    a,
                    backendDto?.queries || []
                  );
                  if (changeType === "POST") {
                    await QueryClient.postAnswer({
                      queryId: queryId,
                      answer: a.answer!,
                      signupId: a.signupId,
                    });
                  } else if (changeType === "PUT") {
                    await QueryClient.putAnswer(
                      {
                        queryId: queryId,
                        answer: a.answer || "",
                        signupId: a.signupId,
                      },
                      a.answerId!
                    );
                  } else if (changeType === "DELETE") {
                    await QueryClient.deleteAnswer(a.answerId!);
                  }
                }
                props.reloadFraction(queryId);
                setMakeChanges(false);
                setAwaitingUpdates(false);
              }}
            >
              <Spinner hidden={!awaitingUpdates} />
              Spara
            </Button>
          </HStack>
        </VStack>
      );
    } else {
      const countDown = backendDto.max_total_count
        ? `(Antal kvar: ${backendDto.max_total_count - backendDto.total_count})`
        : "";
      return (
        <VStack alignItems={"left"}>
          <Text
            hidden={backendDto.max_total_count == null}
            fontSize={"20px"}
            width={"100%"}
            textAlign={"center"}
            marginBottom={"20px"}
          >
            {backendDto?.question} {countDown}
          </Text>
          <Text
            hidden={backendDto.max_total_count != null}
            fontSize={"20px"}
            width={"100%"}
            textAlign={"center"}
            marginBottom={"30px"}
          >
            {backendDto?.question}
          </Text>
          <Grid templateColumns="1fr 3fr" gap={2} width="100%">
            <Text wordBreak="break-word" whiteSpace="normal">
              {noAnswerOption}
            </Text>
            <Text>
              {answers
                .filter((a) => !a.answer)
                .map((a) => a.signupName)
                .join(", ")}
            </Text>
          </Grid>
          {backendDto.options.map((option) => {
            const filteredAnswers = answers
              .filter((a) => a.answer === option)
              .map((a) => a.signupName);
            return (
              <Grid key={option} templateColumns="1fr 3fr" gap={2} width="100%">
                <Text>{option}</Text>
                <Text>{filteredAnswers.join(", ")}</Text>
              </Grid>
            );
          })}
          <Button
            isDisabled={!updatable}
            onClick={() => setMakeChanges(updatable)}
          >
            Ändra/uppdatera
          </Button>
          {session.hasAtLeastRole(Role.ADMIN) && (
            <Button
              as="a"
              href={`/api/posts/query/${queryId}/answers/count`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Hämta alla svar
            </Button>
          )}
        </VStack>
      );
    }
  } else if (backendDto.error && props.fractionProps.state) {
    const countDown = backendDto.max_total_count
      ? `(Antal kvar: ${backendDto.max_total_count - backendDto.total_count})`
      : "";
    const err = backendDto.error;
    const reasons = props.fractionProps.state.errorExplanation;
    const errorText =
      err === FailReasonE.NOT_AUTHENTICATED
        ? reasons.notAuthenticated
        : err == FailReasonE.ANSWER_DEADLINE_REACHED
        ? reasons.answerTimeoutReached
        : err == FailReasonE.MAX_ANSWER_COUNT_REACHED
        ? reasons.maxAnswerCountReached
        : err == FailReasonE.NO_SIGNUP
        ? reasons.noSignup
        : "Något gick fel";
    return (
      <VStack>
        <Text
          hidden={backendDto.max_total_count == null}
          fontSize={"20px"}
          width={"100%"}
          textAlign={"center"}
          marginBottom={"20px"}
        >
          {backendDto?.question} {countDown}
        </Text>
        <Text
          hidden={backendDto.max_total_count != null}
          fontSize={"20px"}
          width={"100%"}
          textAlign={"center"}
          marginBottom={"20px"}
        >
          {backendDto?.question}
        </Text>
        <Text fontSize={"16px"} textColor={"red"}>
          {errorText}
        </Text>
        {session.hasAtLeastRole(Role.ADMIN) && (
          <Button
            as="a"
            href={`/api/posts/query/${queryId}/answers/count`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Hämta alla svar
          </Button>
        )}
        {session.role == null && (
          <LoginModalBody
            onClose={() => {
              props.reloadFraction(props.fractionProps.state?.queryId!);
            }}
          />
        )}
      </VStack>
    );
  }

  return <Text>Something went wrong</Text>;
  //return null;
}

function AnswerInput(props: {
  onChange: (ans: string | null) => void;
  noAnswerOption: string | null;
  options: Array<string>;
  value: string | undefined;
}) {
  if (props.options.length === 0) {
    return (
      <Input
        placeholder="Svar"
        value={props.value}
        onChange={(e) => {
          props.onChange(e.currentTarget.value);
        }}
      />
    );
  } else {
    return (
      <Select
        minW={100}
        value={props.value}
        onChange={(event) => {
          const answer = event.target?.value;
          const noAnswerOption = props.noAnswerOption ?? "";
          props.onChange(answer === noAnswerOption ? null : answer);
        }}
      >
        <option value={props.noAnswerOption || ""}>
          {props.noAnswerOption ?? ""}
        </option>
        {props.options.map((o) => {
          return (
            <option key={o} value={`${o}`}>
              {o}
            </option>
          );
        })}
      </Select>
    );
  }
}

function SignupSelector(props: {
  competitionIdValue: number | null;
  disciplineIdsValue: Array<number>;
  onChange: (
    competitionId: number | null,
    disciplineIds: Array<number>
  ) => void;
}) {
  const [competitions, isLoadingCompetitions] = useAwaitResponse(
    [] as Array<Competition_GET>,
    () => {
      return CompetitionClient.getCompetitions();
    },
    (response) => response.body ?? []
  );

  const [disciplines, setDisciplines] = useState<Array<Discipline_GET>>([]);

  // Get all disciplines for selected competitions
  useEffect(() => {
    if (props.competitionIdValue != null) {
      const competitionTags = competitions
        .filter((c) => c.id === props.competitionIdValue)
        .filter((c) => c.tag != null)
        .map((c) => c.tag!);
      if (competitionTags) {
        DisciplineClient.getDisciplines(competitionTags[0]).then((response) => {
          setDisciplines(response.body ?? []);
        });
      }
    }
  }, [props.competitionIdValue, competitions]);

  return (
    <VStack borderWidth={"2px"} borderColor={"blue"} borderStyle={"dashed"}>
      <Select
        minW={100}
        value={props.competitionIdValue || ""}
        placeholder={"Välj tävling"}
        onChange={(event) => {
          const newVal = event.target?.value;
          if (newVal == null || newVal.length === 0) {
            props.onChange(null, props.disciplineIdsValue);
            return;
          }
          const matchingCompetitions = competitions.filter(
            (c) => `${c.id}` == event.currentTarget.value
          );
          const competitionId =
            matchingCompetitions.length > 0 ? matchingCompetitions[0].id : null;
          console.log(matchingCompetitions, competitionId);
          props.onChange(competitionId, props.disciplineIdsValue);
        }}
      >
        {competitions.map((c) => {
          return (
            <option key={c.id} value={`${c.id}`}>
              {c.tag}
            </option>
          );
        })}
      </Select>
      {props.competitionIdValue && disciplines.length > 0 && (
        <MultiSelectNumber
          initialSelectValue={props.disciplineIdsValue}
          displayValues={disciplines.map((d) => d.name)}
          values={disciplines.map((d) => d.id)}
          title="Välj klasser"
          onChange={(disciplineIds) => {
            props.onChange(
              props.competitionIdValue,
              disciplineIds as Array<number>
            );
          }}
        />
      )}
    </VStack>
  );
}

function convertToIntegerOrNull(value: string): number | null {
  const convertedValue = parseInt(value, 10); // Base 10 for integer conversion

  // Return the converted integer if it's valid, otherwise return undefined
  return isNaN(convertedValue) ? null : convertedValue;
}

export const QueryFraction = (props: FractionProps<QueryFractionState>) => {
  return (
    <PostFraction {...props}>
      <QueryEditorAndViewer />
    </PostFraction>
  );
};
